import React, {useEffect, useState} from 'react';
import qs from 'qs';
import {useDispatch, useSelector} from 'react-redux';
import {useNavigate, useLocation} from 'react-router';
import {Helmet} from 'react-helmet';
import {useIntl} from 'react-intl';
import {Link} from 'react-router-dom';
import isEmpty from 'lodash.isempty';
import Loading from '../shared/Loading/Loading';
import useJoinMessage from '../../hooks/useJoinMessage';
import ChangePassword from '../AccountSettings/ChangePassword/ChangePassword';
import {
    fetchPasswordPolicyByUserId,
    resetPasswordWithToken,
    selectIsFetchingPasswordPolicy,
    selectIsPasswordUpdating,
    selectPasswordPolicyError,
    selectPasswordUpdateError
} from '../Login/userSlice';
import {DEFAULT_PASSWORD_POLICY, LOGIN_PATH, PASSWORD_RECOVERY_PATH} from '../../../constants';
import useNotifications from '../../hooks/useNotifications';
import useErrorNotification from '../../hooks/useErrorNotification';
import './passwordReset.less';

export default function PasswordReset() {
    const intl = useIntl();
    const {showSuccessAlert} = useNotifications();
    const joinMessage = useJoinMessage();
    const dispatch = useDispatch();
    const location = useLocation();
    const navigate = useNavigate();
    const passwordPolicyError = useSelector(selectPasswordPolicyError);
    const passwordUpdateError = useSelector(selectPasswordUpdateError);
    const isFetchingPasswordPolicy = useSelector(selectIsFetchingPasswordPolicy);
    const isPasswordUpdating = useSelector(selectIsPasswordUpdating);

    const [token, setToken] = useState('');
    const [userId, setUserId] = useState('');
    const [passwordPolicy, setPasswordPolicy] = useState({});
    const error = passwordPolicyError;
    const isLoading = isFetchingPasswordPolicy || isPasswordUpdating;

    useErrorNotification(passwordPolicyError);
    useErrorNotification(passwordUpdateError);

    useEffect(() => {
        const {passwordToken, user: userIdFromUrl} = qs.parse(location.search, {ignoreQueryPrefix: true});
        setToken(passwordToken);
        setUserId(userIdFromUrl);

        async function getPasswordPolicy() {
            if (userIdFromUrl) {
                const policyData = await dispatch(fetchPasswordPolicyByUserId(userIdFromUrl));
                setPasswordPolicy(policyData);
            }
        }

        getPasswordPolicy();
    }, [location.search]);

    const handleSubmit = async (form, shouldAllowUpdatePassword) => {
        if (shouldAllowUpdatePassword && userId) {
            const wasSuccessful = await dispatch(resetPasswordWithToken({
                userId,
                newPassword: form.newPassword,
                newPasswordConfirmation: form.confirmedPassword,
                passwordResetToken: token
            }));
            if (wasSuccessful) {
                showSuccessAlert(intl.formatMessage({
                    id: 'page.admin.userManagement.passwordHasBeenReset',
                    defaultMessage: 'Password has been successfully reset. Please log in with your new password.'
                }));
                navigate(LOGIN_PATH);
            }
        }
    };

    const hasRequiredParams = token && userId;

    return (
        <div className="password-reset login-forms">
            <Helmet>
                <title>{joinMessage('esl.generic.reset_password', '-', 'page.title')}</title>
            </Helmet>
            <div className="container">
                <Loading isLoading={isLoading} />
                <h1 className="title">
                    {intl.formatMessage({id: 'esl.generic.reset_password', defaultMessage: ''})}
                </h1>
                <div className="content">
                    {(error || !hasRequiredParams) && (
                        <div className="password-recovery-link-container">
                            {intl.formatMessage({id: 'esl.generic.oops_take_me_back_login', defaultMessage: ''}, {
                                link: (
                                    <Link
                                        to={`${PASSWORD_RECOVERY_PATH}`}
                                        className="password-recovery-link"
                                    >
                                        {intl.formatMessage({id: 'esl.generic.recover_password', defaultMessage: ''})}
                                    </Link>
                                )
                            })}
                        </div>
                    )}
                    {!error && hasRequiredParams && (
                        <ChangePassword
                            isResetPassword
                            updatePassword={handleSubmit}
                            passwordPolicy={isEmpty(passwordPolicy) ? DEFAULT_PASSWORD_POLICY : passwordPolicy}
                            registration
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
